// Fields from history and AI columns: the /api/derived-* calls
// (apps/server/src/derived.mjs) and turning their values into what the
// library's DerivedValueCell shows. Free of React, so it is unit tested
// (derivedApi.test.ts).
import type { DerivedEvidence, DerivedValue, SourceOption } from '@olytic/foundation';

import { type ObjectKey, OBJECTS } from '../navigation';

/** Every object can have fields from history and AI columns (migration 0041). */
export const DERIVABLE_OBJECTS: readonly ObjectKey[] = OBJECTS.map((o) => o.key);
export type DerivableObject = ObjectKey;

export function isDerivable(object: string): object is DerivableObject {
  return (DERIVABLE_OBJECTS as readonly string[]).includes(object);
}

export type OutputType = 'text' | 'long_text' | 'number' | 'currency' | 'date' | 'boolean' | 'picklist';
export type Source = 'fields' | 'meetings' | 'transcripts' | 'emails' | 'calls' | 'messages' | 'notes' | 'documents';

export const OUTPUT_TYPE_LABELS: Record<OutputType, string> = {
  text: 'Text',
  long_text: 'Long text',
  number: 'Number',
  currency: 'Currency',
  date: 'Date',
  boolean: 'Yes / no',
  picklist: 'Picklist',
};

/** What a field may read (derived.mjs SOURCES), as the form offers them. */
export const SOURCE_OPTIONS: readonly (SourceOption & { value: Source })[] = [
  { value: 'fields', label: 'Details', description: 'The record’s own fields', group: 'This record' },
  { value: 'meetings', label: 'Meeting summaries', group: 'Conversations' },
  { value: 'transcripts', label: 'Meeting transcripts', description: 'Every word said; long', group: 'Conversations' },
  { value: 'emails', label: 'Emails', group: 'Conversations' },
  { value: 'calls', label: 'Calls', group: 'Conversations' },
  { value: 'messages', label: 'Messages', description: 'Slack, LinkedIn and texts', group: 'Conversations' },
  { value: 'notes', label: 'Notes', group: 'Conversations' },
  { value: 'documents', label: 'Documents', description: 'Files attached to the record', group: 'Files' },
];

/** Objects whose records have history: meetings, activities and documents link to them. */
const HISTORY_OBJECTS: readonly string[] = ['accounts', 'contacts', 'opportunities', 'projects', 'meetings'];
export const hasHistory = (object: string): boolean => HISTORY_OBJECTS.includes(object);

/** The sources a field on this object may read: only its own fields when it has no history. */
export function sourcesFor(object: string): readonly (SourceOption & { value: Source })[] {
  return hasHistory(object) ? SOURCE_OPTIONS : SOURCE_OPTIONS.filter((o) => o.value === 'fields');
}

export function defaultSources(object: string): Source[] {
  return hasHistory(object) ? ['meetings', 'transcripts', 'emails', 'notes'] : ['fields'];
}

/** Sources kept as one form value: "meetings,emails", in SOURCE_OPTIONS order. */
export function sourcesFromText(text: string | null | undefined): Source[] {
  const chosen = (text ?? '').split(',');
  return SOURCE_OPTIONS.map((o) => o.value).filter((v) => chosen.includes(v));
}

export interface DerivedField {
  id: string;
  object: DerivableObject;
  key: string;
  label: string;
  instruction: string;
  output_type: OutputType;
  options: string[];
  sources: Source[];
  include_related: boolean;
  status: 'active' | 'paused';
  total: number;
  filled: number;
  pending: number;
  errors: number;
}

export interface ApiEvidence {
  kind: 'meeting' | 'activity' | 'document' | 'record';
  id: string;
  quote?: string;
  at?: string;
  label?: string;
}

export interface ApiDerivedValue {
  fieldId: string;
  recordId: string;
  value: unknown;
  state: DerivedValue['state'];
  evidence: ApiEvidence[];
  confidence: number | null;
  stale: boolean;
  edited: boolean;
  computedAt: string | null;
  error: string | null;
}

export interface FieldInput {
  object?: DerivableObject;
  label: string;
  instruction: string;
  output_type: OutputType;
  options: string[];
  sources: Source[];
  include_related: boolean;
}

/** The column key an AI column takes in a list: never clashes with a real column. */
export const aiColumnKey = (field: Pick<DerivedField, 'key'>): string => `ai:${field.key}`;

/** The hint on a queued value: the server's AI fills it on its next capture run or process call. */
export const QUEUED_HINT = 'Queued: the AI fills it on its next run';

const DATE_FORMAT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const CURRENCY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** A value as text for its output type; null when there is none. */
export function formatDerived(type: OutputType, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  switch (type) {
    case 'boolean':
      return value === true ? 'Yes' : value === false ? 'No' : null;
    case 'currency':
      return typeof value === 'number' ? CURRENCY.format(value) : null;
    case 'number':
      return typeof value === 'number' ? value.toLocaleString('en-US') : null;
    case 'date':
      return typeof value === 'string' && !Number.isNaN(Date.parse(value)) ? DATE_FORMAT.format(new Date(value)) : null;
    default:
      return typeof value === 'string' ? value : String(value);
  }
}

/** The raw value an inline editor starts from. */
export function rawDerived(type: OutputType, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (type === 'boolean') return value === true ? 'true' : value === false ? 'false' : '';
  return String(value);
}

/** Where evidence links to; a record's own fields have no page of their own. */
const EVIDENCE_PATH: Partial<Record<ApiEvidence['kind'], ObjectKey>> = {
  meeting: 'meetings',
  activity: 'activities',
  document: 'documents',
};
const EVIDENCE_NOUN: Record<ApiEvidence['kind'], string> = { meeting: 'Meeting', activity: 'Activity', document: 'Document', record: 'Record details' };

/** What DerivedValueCell shows for one value. */
export function toDerivedValue(field: Pick<DerivedField, 'output_type'>, value: ApiDerivedValue): DerivedValue {
  const evidence: DerivedEvidence[] = value.evidence.map((e) => ({
    label: [e.label ?? EVIDENCE_NOUN[e.kind], e.at ? formatDerived('date', e.at.slice(0, 10)) : null].filter(Boolean).join(' · '),
    ...(e.quote ? { quote: e.quote } : {}),
    href: EVIDENCE_PATH[e.kind] ? `/${EVIDENCE_PATH[e.kind]}/${encodeURIComponent(e.id)}` : null,
  }));
  return {
    state: value.state,
    display: formatDerived(field.output_type, value.value),
    evidence,
    confidence: value.confidence,
    computedAt: value.computedAt ? formatDerived('date', value.computedAt.slice(0, 10)) : null,
    stale: value.stale,
    edited: value.edited,
    error: value.error,
  };
}

/** "212 of 340 filled", with what is still waiting or failed. */
export function progressText(field: Pick<DerivedField, 'total' | 'filled' | 'pending' | 'errors' | 'status'>): string {
  if (field.total === 0) return 'No records yet';
  const parts = [`${field.filled.toLocaleString('en-US')} of ${field.total.toLocaleString('en-US')} filled`];
  if (field.pending > 0) parts.push(`${field.pending.toLocaleString('en-US')} waiting`);
  if (field.errors > 0) parts.push(`${field.errors.toLocaleString('en-US')} failed`);
  return parts.join(' · ');
}

/** Options typed as "High, Medium, Low". */
export function parseOptions(text: string | null): string[] {
  return [...new Set((text ?? '').split(',').map((o) => o.trim()).filter(Boolean))];
}

// ── Calls ──────────────────────────────────────────────────────────────────

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

async function failure(response: Response, fallback: string): Promise<Error> {
  try {
    const payload = (await response.json()) as { error?: unknown };
    if (typeof payload.error === 'string') return new Error(payload.error);
  } catch {
    // Not JSON.
  }
  return new Error(fallback);
}

async function json<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) throw await failure(response, fallback);
  return (await response.json()) as T;
}

const jsonInit = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

export async function fetchFields(fetcher: Fetch, object?: DerivableObject, init?: RequestInit): Promise<DerivedField[]> {
  const url = object ? `/api/derived-fields?object=${object}` : '/api/derived-fields';
  return (await json<{ fields: DerivedField[] }>(await fetcher(url, init), 'Could not load fields from history.')).fields;
}

export async function fetchValues(
  fetcher: Fetch,
  object: DerivableObject,
  ids: readonly string[],
  init?: RequestInit,
): Promise<ApiDerivedValue[]> {
  if (ids.length === 0) return [];
  const url = `/api/derived-values?object=${object}&ids=${ids.map(encodeURIComponent).join(',')}`;
  return (await json<{ values: ApiDerivedValue[] }>(await fetcher(url, init), 'Could not load AI values.')).values;
}

export async function createField(fetcher: Fetch, input: FieldInput): Promise<DerivedField> {
  return (await json<{ field: DerivedField }>(await fetcher('/api/derived-fields', jsonInit('POST', input)), 'Could not save the field.')).field;
}

export async function updateField(fetcher: Fetch, id: string, changes: Partial<FieldInput> & { status?: 'active' | 'paused' }): Promise<DerivedField> {
  return (
    await json<{ field: DerivedField }>(await fetcher(`/api/derived-fields/${encodeURIComponent(id)}`, jsonInit('PATCH', changes)), 'Could not save the field.')
  ).field;
}

export async function deleteField(fetcher: Fetch, id: string): Promise<void> {
  await json(await fetcher(`/api/derived-fields/${encodeURIComponent(id)}`, { method: 'DELETE' }), 'Could not delete the field.');
}

export async function rerunField(fetcher: Fetch, id: string, recordIds?: string[]): Promise<number> {
  return (
    await json<{ queued: number }>(
      await fetcher(`/api/derived-fields/${encodeURIComponent(id)}/rerun`, jsonInit('POST', recordIds ? { recordIds } : {})),
      'Could not re-run the field.',
    )
  ).queued;
}

export async function saveValue(fetcher: Fetch, fieldId: string, recordId: string, value: string | null): Promise<ApiDerivedValue> {
  const body = value === null ? { clear: true } : { value };
  return (
    await json<{ value: ApiDerivedValue }>(
      await fetcher(`/api/derived-values/${encodeURIComponent(fieldId)}/${encodeURIComponent(recordId)}`, jsonInit('PUT', body)),
      'Could not save the value.',
    )
  ).value;
}
